import React from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaSection from '../components/CtaSection';
import ScrollReveal from '../components/animated/ScrollReveal';
import Text from '../components/Text';
import { colors } from '../theme/designTokens';
import { capabilities, getCapabilityBySlug } from '../data/capabilities';

export const CapabilitiesHub = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />
      <PageHero
        variant="dark"
        eyebrow="Capabilities"
        title="Strategic Capabilities."
        titleHighlight="Not services."
        subtitle="Twelve capabilities that form an integrated resilience system—designed for boards and operators who need more than placement."
        badges={['Risk Intelligence', 'Protection Architecture', 'Continuity']}
      />

      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="The System"
              title="Every capability feeds AERA™."
              highlightWords={['AERA™']}
              subtitle="From enterprise risk intelligence to resilience analytics—each capability strengthens Anticipate, Assess, Architect, Activate, and Advance."
            />
          </ScrollReveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {capabilities.map((cap, i) => (
              <Box
                key={cap.slug}
                component="button"
                type="button"
                onClick={() => navigate(`/capabilities/${cap.slug}`)}
                sx={{
                  textAlign: 'left',
                  p: 3,
                  cursor: 'pointer',
                  border: `1px solid ${colors.borderLight}`,
                  background: colors.white,
                  '&:hover': { borderColor: colors.navy },
                }}
              >
                <Text variant="eyebrow" sx={{ color: colors.slate, mb: 1, display: 'block' }}>
                  {String(i + 1).padStart(2, '0')}
                </Text>
                <Text variant="h4" color="primary" sx={{ mb: 1 }}>
                  {cap.title}
                </Text>
                <Text variant="bodySm" color="secondary">
                  {cap.summary}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <CtaSection />
      <Footer />
    </Box>
  );
};

export const CapabilityDetail = () => {
  const { slug } = useParams();
  const cap = getCapabilityBySlug(slug);
  if (!cap) return <Navigate to="/capabilities" replace />;

  return (
    <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />
      <PageHero
        variant="dark"
        eyebrow="Capability"
        title={cap.title}
        subtitle={cap.summary}
        badges={['Strategic Capability', 'AERA™ Aligned']}
        minHeight={{ xs: 300, md: 400 }}
      />
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 10 } }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <ScrollReveal>
            <Text variant="body" color="secondary" sx={{ mb: 4, display: 'block', fontSize: { md: '1.1rem' } }}>
              {cap.body}
            </Text>
            <Text variant="h3" color="primary" sx={{ mb: 2 }}>
              How it connects to AERA™
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 4, display: 'block' }}>
              This capability strengthens ALEgION’s Enterprise Resilience Architecture—informing anticipation, sharpening assessment, shaping architecture, supporting activation, and feeding continuous advancement.
            </Text>
            <Box
              component={RouterLink}
              to="/capabilities"
              sx={{ color: colors.navy, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              ← All Capabilities
            </Box>
          </ScrollReveal>
        </Box>
      </Box>
      <CtaSection secondaryPath="/framework" secondaryLabel="See AERA™" />
      <Footer />
    </Box>
  );
};

export default CapabilitiesHub;
